import { useCallback, useEffect, useMemo, useState } from "react";
import { getStoredSelectedAccountId } from "../Constants/accountTypes";
import {
  fetchIbAllCurrencyBalances,
  fetchIbForeignBalances,
} from "../Services/ibAccountBalancesApi";
import { useFxQuotes } from "./useFxQuotes";

const parseFxRateFromDisplay = (price) => {
  if (price == null || price === "") return null;

  const normalized = String(price).replace(/\./g, "").replace(",", ".");
  const numeric = Number(normalized);

  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const applyRates = (currencies, rateByAsset) =>
  currencies.map((currency) => ({
    ...currency,
    rateToBrl:
      currency.code === "BRL"
        ? 1
        : rateByAsset[currency.id] ?? currency.rateToBrl ?? 0,
  }));

/**
 * Live multi-currency balances for Conta internacional (active IB account via MobileService).
 * Refreshes when `accountKey` changes (e.g. HomeAccountSelector active account).
 */
export const useContaInternacionalBalances = ({ accountKey } = {}) => {
  const resolvedAccountKey =
    accountKey !== undefined ? accountKey : getStoredSelectedAccountId();

  const { quotes, loading: quotesLoading, usingFallback, reload: reloadQuotes } = useFxQuotes();
  const [allCurrenciesRaw, setAllCurrenciesRaw] = useState([]);
  const [foreignCurrenciesRaw, setForeignCurrenciesRaw] = useState([]);
  const [balancesLoading, setBalancesLoading] = useState(true);
  const [balancesError, setBalancesError] = useState(null);

  const rateByAsset = useMemo(() => {
    const map = {};
    quotes.forEach((quote) => {
      const rate = parseFxRateFromDisplay(quote.price);
      if (rate != null) {
        map[quote.asset] = rate;
      }
    });
    return map;
  }, [quotes]);

  const reloadBalances = useCallback(async () => {
    setBalancesLoading(true);
    setBalancesError(null);
    try {
      const [foreign, all] = await Promise.all([
        fetchIbForeignBalances(resolvedAccountKey),
        fetchIbAllCurrencyBalances(resolvedAccountKey),
      ]);
      setForeignCurrenciesRaw(foreign);
      setAllCurrenciesRaw(all.length ? all : foreign);
    } catch (error) {
      setForeignCurrenciesRaw([]);
      setAllCurrenciesRaw([]);
      setBalancesError(error);
    } finally {
      setBalancesLoading(false);
    }
  }, [resolvedAccountKey]);

  useEffect(() => {
    reloadBalances();
  }, [reloadBalances, resolvedAccountKey]);

  const currencies = useMemo(
    () => applyRates(foreignCurrenciesRaw, rateByAsset),
    [foreignCurrenciesRaw, rateByAsset],
  );

  const allCurrencies = useMemo(
    () => applyRates(allCurrenciesRaw, rateByAsset),
    [allCurrenciesRaw, rateByAsset],
  );

  const totalBrl = useMemo(
    () =>
      currencies.reduce(
        (sum, currency) => sum + Number(currency.balance || 0) * Number(currency.rateToBrl || 0),
        0,
      ),
    [currencies],
  );

  const getCurrencyById = useCallback(
    (currencyId) => {
      const id = String(currencyId || "").toLowerCase();
      return (
        allCurrencies.find((currency) => currency.id === id) ||
        currencies.find((currency) => currency.id === id) ||
        null
      );
    },
    [allCurrencies, currencies],
  );

  const reload = useCallback(() => {
    reloadQuotes();
    return reloadBalances();
  }, [reloadQuotes, reloadBalances]);

  return {
    currencies,
    allCurrencies,
    totalBrl,
    loading: balancesLoading || (quotesLoading && !currencies.length),
    balancesLoading,
    quotesLoading,
    usingFallback,
    error: balancesError,
    getCurrencyById,
    reload,
    reloadBalances,
  };
};
